import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { EntityCard } from '../components/EntityCard';
import { EntityEditor } from '../components/EntityEditor';
import { Search, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';

export function Entities() {
    const [isEditorOpen, setIsEditorOpen] = React.useState(false);
    const [editingEntity, setEditingEntity] = React.useState<any>(undefined);
    const [searchQuery, setSearchQuery] = React.useState('');


    const { data, isLoading, error } = useQuery({
        queryKey: ['entities'],
        queryFn: () => api.getEntities(),
    });

    const handleEdit = (entity: any) => {
        setEditingEntity(entity);
        setIsEditorOpen(true);
    };

    const handleClose = () => {
        setIsEditorOpen(false);
        setEditingEntity(undefined);
    };

    // Filter and sort entities
    const filteredEntities = React.useMemo(() => {
        if (!data?.data) return [];

        let filtered = [...data.data];

        // Filter by search query (match against aliases or code)
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter((entity: any) => {
                if (entity.code.toLowerCase().includes(query)) return true;
                if (entity.alias && Array.isArray(entity.alias)) {
                    return entity.alias.some((alias: string) =>
                        alias.toLowerCase().includes(query)
                    );
                }
                return false;
            });
        }

        // Sort by jd_level (L1 -> L2 -> L3) and then by code
        filtered.sort((a: any, b: any) => {
            // If we want a true TOC, we might want to sort by Code primarily if the user names them like "10.00 Area", "11.00 Category", etc.
            // But if we just want strictly by level first (though that breaks TOC flow usually)...
            // Actually, usually TOC means they are sorted by some ID/Code that implies hierarchy.
            // If I just sort by Created At it might be random. 
            // Let's assume sorting by Code is the most logical "TOC" default if no explicit order.
            return a.code.localeCompare(b.code);
        });

        return filtered;
    }, [data, searchQuery]);

    if (isLoading) return <div className="flex justify-center p-8">Loading...</div>;
    if (error) return <div className="text-red-500 p-8">Error loading entities</div>;

    const getIndentClass = (level?: string) => {
        switch (level) {
            case 'L2': return 'ml-6';
            case 'L3': return 'ml-12';
            default: return ''; // L1 or undefined
        }
    };

    return (
        <div className="w-full px-4 pt-2 pb-20">
            <div className="max-w-3xl mx-auto space-y-2">
                <div className="flex items-center gap-4 sticky top-[calc(3.5rem+env(safe-area-inset-top))] bg-white/80 dark:bg-black/80 backdrop-blur-md z-10 py-2 border-b border-zinc-100 dark:border-zinc-900 mb-2 -mx-4 px-4 md:px-0 md:mx-0">
                    <h1 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 shrink-0">Table of Contents</h1>

                    <div className="flex-1 relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 pointer-events-none" />
                        <Input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-8 h-8 text-sm bg-zinc-50 dark:bg-zinc-900/50 border-none shadow-none focus-visible:ring-1"
                        />
                    </div>

                    <button
                        onClick={() => setIsEditorOpen(true)}
                        className="p-1.5 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 transition-colors shrink-0"
                        aria-label="New entity"
                    >
                        <Plus className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex flex-col gap-0.5">
                    {filteredEntities.map((entity: any) => (
                        <div key={entity.entity_id} className={getIndentClass(entity.jd_level)}>
                            <EntityCard
                                entity={entity}
                                onEdit={handleEdit}
                            />
                        </div>
                    ))}
                    {filteredEntities.length === 0 && (
                        <div className="text-center text-zinc-500 py-10">
                            {searchQuery
                                ? 'No entities match your search.'
                                : 'No entities yet. Start building your TOC!'}
                        </div>
                    )}
                </div>
            </div>

            <EntityEditor
                isOpen={isEditorOpen}
                onClose={handleClose}
                initialData={editingEntity}
            />
        </div>
    );
}
